/**
 * Product resource guards.
 *
 * These values protect browser responsiveness and local storage. They are not
 * controller-design requirements and they never create a met/not-met result.
 * The rationale and boundary behaviour are published in docs/KNOWN_LIMITATIONS.md.
 */
export const RESOURCE_LIMITS = Object.freeze({
  /** Two megabytes is ample for the versioned scenario schema while limiting hostile local imports. */
  scenarioImportBytes: 2 * 1024 * 1024,
  /** Supports five standard 20 s, 1 ms workloads in one history while bounding browser memory. */
  simulationOutputPoints: 100_000,
  /** Prevents a small JSON file from creating pathological event-grid work. */
  referenceProfileEvents: 1_000,
  /** Full histories are retained, so the browser-local workspace is bounded explicitly. */
  retainedRuns: 100,
  /** Matches the visible search guard and keeps finite Cartesian searches reviewable. */
  tuningCandidates: 5_000,
});

export function estimateNominalOutputPoints(startTime: number, endTime: number, nominalStep: number): number {
  if (![startTime, endTime, nominalStep].every(Number.isFinite) || nominalStep <= 0 || endTime <= startTime) {
    return Number.POSITIVE_INFINITY;
  }
  const intervals = Math.ceil((endTime - startTime) / nominalStep);
  return Number.isSafeInteger(intervals) ? intervals + 1 : Number.POSITIVE_INFINITY;
}

export function formatByteLimit(bytes: number): string {
  if (bytes % (1024 * 1024) === 0) return `${bytes / (1024 * 1024)} MB`;
  if (bytes % 1024 === 0) return `${bytes / 1024} kB`;
  return `${bytes} bytes`;
}
